/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.footerContent = this.querySelector('.js-footer-content');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchCache = new Map();
      this.openRevision = 0;
      this.renderedProductUrl = null;
      this.pointerOverHandler = this.handlePointerOver.bind(this);
      this.pointerOutHandler = this.handlePointerOut.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);
      this.delayedOpenTimer = null;

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.addEventListener('pointerover', this.pointerOverHandler, { passive: true });
        document.addEventListener('pointerout', this.pointerOutHandler, { passive: true });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.removeEventListener('pointerover', this.pointerOverHandler);
      document.removeEventListener('pointerout', this.pointerOutHandler);
      window.clearTimeout(this.prefetchTimer);
      window.clearTimeout(this.delayedOpenTimer);
    }

    /**
     * Returns one cached product request per URL.
     * @param {string} url - Product URL.
     * @returns {Promise<string>}
     */
    fetchProduct(url) {
      if (!this.fetchCache.has(url)) {
        if (this.fetchCache.size >= 12) {
          this.fetchCache.delete(this.fetchCache.keys().next().value);
        }
        const request = fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .catch((error) => {
            this.fetchCache.delete(url);
            throw error;
          });
        this.fetchCache.set(url, request);
      }
      return this.fetchCache.get(url);
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handlePointerOver(evt) {
      const button = evt.target.closest('.js-quick-add');
      if (!button || button.contains(evt.relatedTarget)) return;
      window.clearTimeout(this.prefetchTimer);
      this.prefetchTimer = window.setTimeout(() => {
        this.fetch = {
          url: button.dataset.productUrl,
          promise: this.fetchProduct(button.dataset.productUrl)
        };
      }, 180);
    }

    handlePointerOut(evt) {
      const button = evt.target.closest('.js-quick-add');
      if (!button || button.contains(evt.relatedTarget)) return;
      window.clearTimeout(this.prefetchTimer);
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      const opener = evt.target.closest('.js-quick-add');
      if (!opener) return;
      window.clearTimeout(this.delayedOpenTimer);

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        this.delayedOpenTimer = window.setTimeout(() => {
          this.delayedOpenTimer = null;
          this.backBtn.hidden = false;
          this.open(opener);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(opener);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        this.updateMedia(evt.detail.variant.featured_media);
      }
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      const requestedUrl = opener.dataset.productUrl;
      const revision = ++this.openRevision;
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (
        this.renderedProductUrl === requestedUrl
        && this.content.hasChildNodes()
        && !this.classList.contains('is-loading')
      ) {
        this.productUrl = requestedUrl;
        super.open(opener);
        if (opener.dataset.selectedColor) this.setActiveVariant(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = requestedUrl;
      this.renderedProductUrl = null;
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== requestedUrl) {
        this.fetch = {
          url: requestedUrl,
          promise: this.fetchProduct(requestedUrl)
        };
      }
      const request = this.fetch;

      try {
        const html = await request.promise;
        if (revision !== this.openRevision || this.productUrl !== requestedUrl) return;
        const tmpl = document.createElement('template');
        tmpl.innerHTML = html;
        const quickBuyTemplate = tmpl.content.getElementById('quick-buy-template');
        if (!quickBuyTemplate) throw new Error('Quick add content was not found.');
        this.productEl = quickBuyTemplate.content.querySelector('.js-product');
        this.renderedProductUrl = requestedUrl;
        this.renderProduct(opener);
      } catch (error) {
        if (revision !== this.openRevision || this.productUrl !== requestedUrl) return;
        this.fetchCache.delete(requestedUrl);
        this.productUrl = null;
        this.renderedProductUrl = null;
        const alert = document.createElement('p');
        alert.className = 'alert bg-error-bg text-error-text';
        alert.setAttribute('role', 'alert');
        alert.textContent = theme.strings.cartError;
        this.content.replaceChildren(alert);
        this.classList.remove('is-loading');
        this.content.classList.remove('drawer__content--out');
        this.footer.classList.remove('drawer__footer--out');
        console.log(error); // eslint-disable-line
      } finally {
        if (this.fetch?.url === requestedUrl) this.fetch = null;
        opener.removeAttribute('aria-disabled');
      }
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      QuickAddDrawer.convertToQuickBuyContent(this.productEl, this.productUrl);

      this.updateFooter();
      this.updateContent();

      if (opener.dataset.selectedColor) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
      window.initLazyImages();
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} media - Variant featured media object.
     */
    updateMedia(media) {
      if (!media) return;

      const { src } = media.preview_image;
      const container = this.querySelector('.quick-add-info__media');
      const width = container.offsetWidth;
      const aspectRatio = media.preview_image.aspect_ratio;

      const existingId = container.dataset.mediaId;
      const existingImg = container.querySelector('img');

      if (!existingImg || existingId !== `${media.id}`) {
        container.innerHTML = `
          <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${media.alt || ''}">
        `;
        container.dataset.mediaId = media.id;
      }
    }

    /**
     * Renders the markup for the drawer content element.
     */
    updateContent() {
      const productContent = this.productEl.querySelector('.js-product-details');
      if (productContent) {
        this.content.innerHTML = productContent.innerHTML;
      }

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
    }

    /**
     * Renders the markup for the drawer footer element.
     */
    updateFooter() {
      const footerContent = this.productEl.querySelector('.js-footer-content');
      this.footer.classList.remove('quick-add__footer-message');

      if (footerContent && footerContent.hasChildNodes()) {
        this.footerContent.innerHTML = footerContent.innerHTML;
      } else {
        this.footer.classList.add('quick-add__footer-message');
        this.footerContent.innerHTML = `
          <div class="alert quick-add__alert bg-info-bg text-info-text">
            <div class="flex">
              <div>
                <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                  <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
              </div>
            </div>
          </div>`;
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        this.notification.hidden = false;

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }

    /**
     * Convert product template content to quick buy content.
     * @param {Element} container - Container element containing new content to use.
     */
    static convertToQuickBuyContent(container) {
      // Prevent variant picker from updating the URL on change.
      const variantPicker = container.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.dataset.updateUrl = 'false';
      }

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = container.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);

  window.theme = window.theme || {};
  window.theme.quickBuy = window.theme.quickBuy || {};
  window.theme.quickBuy.convertToQuickBuyContent = QuickAddDrawer.convertToQuickBuyContent;
}
